import type { Event } from "@viagogo/cx-sentry";
import { SentryTags } from "../SentryTags";

type FetchContext = {
  options?: {
    noWaf?: boolean;
  };
};

type FetchFailureEvent = Event & {
  tags?: (Event["tags"] & { errorCategory?: string; fetchedUrl?: string; httpStatusCode?: string }) | undefined;
  contexts?: (Event["contexts"] & { fetch?: FetchContext }) | undefined;
  exception?: {
    values?: Array<{
      value?: string;
    }>;
  };
};

const NETWORK_ERRORS_TO_SAMPLE_AT_HIGHER_RATE = [
  /^load failed$/i,
  /^failed to fetch$/i,
  /^networkerror when attempting to fetch resource\.$/i,
] as const;

const ANALYTICS_URLS = [
  "https://wt.viagogo.net/ipeb",
  "https://wt.viagogo.net/iae",
  "https://wt.viagogo.net/icpv",
  "https://pt.stubhub.net/iae",
] as const;

const PAGE_EVENT_BATCH_PATH = "/ipeb";
const LISTINGS_FETCH_400_SAMPLE_RATE = 0.1;
const PAGE_EVENT_BATCH_400_SAMPLE_RATE = 0.001;
const ANALYTICS_400_SAMPLE_RATE = 0.01;

const isAnalyticsUrl = (url?: string): boolean => Boolean(url && ANALYTICS_URLS.some((x) => url.includes(x)));

const isPageEventBatchUrl = (url?: string): boolean => {
  if (!url) return false;

  try {
    return new URL(url, "https://www.stubhub.com").pathname === PAGE_EVENT_BATCH_PATH;
  } catch {
    return false;
  }
};

const getErrorMessage = (event: FetchFailureEvent): string => event.exception?.values?.[0]?.value ?? "";

const getFetchedUrl = (event: FetchFailureEvent): string | undefined => event.tags?.[SentryTags.FETCHED_URL];

const getErrorCategory = (event: FetchFailureEvent): string | undefined => event.tags?.[SentryTags.ERROR_CATEGORY];

const getTaggedStatusCode = (event: FetchFailureEvent): number | undefined => {
  const statusCode = Number(event.tags?.[SentryTags.HTTP_STATUS_CODE]);
  return Number.isSafeInteger(statusCode) ? statusCode : undefined;
};

const getNoWaf = (event: FetchFailureEvent): boolean | undefined => event.contexts?.fetch?.options?.noWaf;

const STATUS_REGEX = /"status"\s*:\s*"?(\d{3})"?/;
const getStatusCode = (event: FetchFailureEvent): number | undefined => {
  const taggedStatusCode = getTaggedStatusCode(event);
  if (taggedStatusCode) return taggedStatusCode;

  const msg = getErrorMessage(event);
  const match = STATUS_REGEX.exec(msg);
  return match ? Number(match[1]) : undefined;
};

const is403Error = (event: FetchFailureEvent): boolean => getStatusCode(event) === 403;
const is400Error = (event: FetchFailureEvent): boolean => getStatusCode(event) === 400;

const shouldDrop403Error = (event: FetchFailureEvent): boolean => {
  // All endpoints are WAF protected unless spec'd with `noWaf = true` (external)
  const noWaf = getNoWaf(event);
  // If noWaf is false, it means this an internal call protected by WAF and 403s are expected & can be dropped at a high rate.
  if (!noWaf) return true;
  // If noWaf is true and it's an Analytics URL, we can ignore because this is a non-critical path
  if (isAnalyticsUrl(getFetchedUrl(event))) return true;
  return false;
};

export const fetchFailureEventProcessor = (event: Event): Event | null => {
  const fetchEvent = event as FetchFailureEvent;
  const is403LikeError = is403Error(fetchEvent);
  const is400LikeError = is400Error(fetchEvent);
  const errorMessage = getErrorMessage(fetchEvent);
  const isNetworkError = NETWORK_ERRORS_TO_SAMPLE_AT_HIGHER_RATE.some((r) => r.test(errorMessage));
  const fetchedUrl = getFetchedUrl(fetchEvent);
  const analyticsUrl = isAnalyticsUrl(fetchedUrl);
  const pageEventBatchUrl = isPageEventBatchUrl(fetchedUrl);
  const isListingsFetchError = getErrorCategory(fetchEvent) === "listingsFetch";

  if (isListingsFetchError && is400LikeError) return Math.random() < LISTINGS_FETCH_400_SAMPLE_RATE ? event : null;
  // More aggressively sample non-critical page-event batch endpoint validation failures.
  if (is400LikeError && pageEventBatchUrl) return Math.random() < PAGE_EVENT_BATCH_400_SAMPLE_RATE ? event : null;
  // Full drop
  if (is403LikeError && shouldDrop403Error(fetchEvent)) return null;
  // 1% sample of Analytics URL 400 errors
  if (is400LikeError && analyticsUrl) return Math.random() < ANALYTICS_400_SAMPLE_RATE ? event : null;
  // 10% sample of general Network errors
  if (isNetworkError) return Math.random() < 0.1 ? event : null;

  return event;
};
